import React, { useState } from 'react';
import { Box, Button, ButtonProps, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { SignalTone, tokens } from '../../../theme/tokens';
import { StagedSourceBadge } from '../../StagedSourceBadge';
import type { StagedFieldSource } from '../../../utils/stagedFieldSource';

/**
 * Building blocks for the admin product detail panel (design_handoff_admin_product_detail).
 * Every colour is a token; the handoff's hex values map onto the five signal tones.
 */

export const EMPTY = '—';

/** 6px status dot. Never used without words beside it. */
export const Dot: React.FC<{ tone: SignalTone; size?: number }> = ({ tone, size = 6 }) => (
  <Box
    component="span"
    aria-hidden
    sx={{ width: size, height: size, borderRadius: '50%', bgcolor: tokens.signal[tone].solid, flex: '0 0 auto', display: 'inline-block' }}
  />
);

/** Small pill with a leading dot: status, health, staged state. */
export const Pill: React.FC<{ tone: SignalTone; children: React.ReactNode; dot?: boolean }> = ({ tone, children, dot = true }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      height: 20,
      px: 1,
      borderRadius: `${tokens.radius.chip}px`,
      bgcolor: tokens.signal[tone].tint,
      color: tokens.signal[tone].ink,
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    {dot && <Dot tone={tone} size={5} />}
    {children}
  </Box>
);

/** Neutral count bubble beside a card title. */
export const CountBubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{ fontSize: 12, color: tokens.ink.muted, bgcolor: tokens.surface.tint, borderRadius: `${tokens.radius.pill}px`, px: 1, py: '1px', fontVariantNumeric: 'tabular-nums' }}
  >
    {children}
  </Box>
);

/** Uppercase field label. */
export const FieldLabel: React.FC<{ children: React.ReactNode; mono?: boolean }> = ({ children, mono }) => (
  <Typography
    component="span"
    sx={
      mono
        ? { fontFamily: tokens.font.mono, fontSize: 11, color: tokens.ink.muted }
        : { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.muted }
    }
  >
    {children}
  </Typography>
);

/** The panel's card: white, hairline border, 12px radius, 14/18 header. */
export const DetailCard: React.FC<{
  title?: React.ReactNode;
  note?: React.ReactNode;
  count?: React.ReactNode;
  actions?: React.ReactNode;
  tone?: SignalTone;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  headerAlign?: 'baseline' | 'center' | 'flex-start';
}> = ({ title, note, count, actions, tone, children, footer, headerAlign = 'baseline' }) => (
  <Box
    sx={{
      border: `1px solid ${tone ? tokens.signal[tone].border : tokens.line.default}`,
      bgcolor: tokens.surface.card,
      borderRadius: `${tokens.radius.card}px`,
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    {(title || actions) && (
      <Box sx={{ display: 'flex', alignItems: headerAlign, gap: 1.25, px: '18px', pt: '14px', pb: '6px', flexWrap: 'wrap' }}>
        {title && <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink.primary }}>{title}</Typography>}
        {count != null && <CountBubble>{count}</CountBubble>}
        {note && <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.faint }}>{note}</Typography>}
        {actions && <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</Box>}
      </Box>
    )}
    {children}
    {footer && (
      <Box sx={{ px: '18px', py: '10px', borderTop: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.surface.sunken, fontSize: 12, color: tokens.ink.muted }}>
        {footer}
      </Box>
    )}
  </Box>
);

/** Tab body: 22px padding on the subtle ground, cards 16px apart. */
export const TabBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: '22px', bgcolor: tokens.surface.subtle, minHeight: '100%', boxSizing: 'border-box' }}>
    {children}
  </Box>
);

/** Auto-fill field grid used by Identity, Availability, Prices and Physical. */
export const FieldGrid: React.FC<{ children: React.ReactNode; min?: number }> = ({ children, min = 190 }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: `repeat(auto-fill, minmax(${min}px, 1fr))` }, gap: '4px 20px', px: '18px', pt: '6px', pb: '14px' }}>
    {children}
  </Box>
);

export interface FieldProps {
  label: React.ReactNode;
  source?: StagedFieldSource | null;
  sourceLabel?: string;
  hint?: React.ReactNode;
  wide?: boolean;
  /** Read view: string or node. Empty strings show the em dash. */
  display?: React.ReactNode;
  /** Edit control; when present it replaces the read view. */
  editor?: React.ReactNode;
  valueSize?: number;
}

/** One labelled value: label + source badge, value or editor, hint. */
export const Field: React.FC<FieldProps> = ({ label, source, sourceLabel, hint, wide, display, editor, valueSize = 14 }) => {
  const empty = display == null || display === '';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: '7px', gridColumn: wide ? '1 / -1' : 'auto', minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <FieldLabel>{label}</FieldLabel>
        <StagedSourceBadge source={source} label={sourceLabel} />
      </Stack>
      {editor ?? (
        <Typography
          component="div"
          sx={{
            fontSize: valueSize,
            lineHeight: 1.45,
            color: empty ? tokens.ink.faint : tokens.ink.primary,
            fontVariantNumeric: 'tabular-nums',
            overflowWrap: 'anywhere',
          }}
        >
          {empty ? EMPTY : display}
        </Typography>
      )}
      {hint && <Typography component="span" sx={{ fontSize: 11.5, lineHeight: 1.45, color: tokens.ink.muted }}>{hint}</Typography>}
    </Box>
  );
};

/** Compact text input sized like the handoff's 34px controls. */
export const TextInput: React.FC<{
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  ariaLabel: string;
  mono?: boolean;
  onKeyDown?: React.KeyboardEventHandler;
  sx?: object;
}> = ({ value, onChange, placeholder, multiline, rows, ariaLabel, mono, onKeyDown, sx }) => (
  <TextField
    size="small"
    fullWidth
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    multiline={multiline}
    minRows={rows}
    onKeyDown={onKeyDown}
    slotProps={{ htmlInput: { 'aria-label': ariaLabel } }}
    sx={{
      '& .MuiInputBase-root': { fontSize: 13.5, borderRadius: `${tokens.radius.small}px`, ...(mono ? { fontFamily: tokens.font.mono, fontSize: 12 } : {}) },
      ...sx,
    }}
  />
);

/** A select over plain string options; a stored value outside the list is kept as an option. */
export const SelectInput: React.FC<{
  value: string;
  options: string[];
  onChange: (next: string) => void;
  ariaLabel: string;
  labels?: Record<string, string>;
  /** Show the options under group headings; `options` still lists every allowed value. */
  groups?: Array<{ label: string; options: Array<{ value: string; label: string }> }>;
}> = ({ value, options, onChange, ariaLabel, labels, groups }) => {
  const opts = value && !options.includes(value) ? [...options, value] : options;
  const legacy = value && !options.includes(value) ? value : '';
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{ htmlInput: { 'aria-label': ariaLabel }, select: { native: true } }}
      sx={{ '& .MuiInputBase-root': { fontSize: 13.5, borderRadius: `${tokens.radius.small}px` } }}
    >
      {!value && <option value="">{EMPTY}</option>}
      {groups ? (
        <>
          {legacy && <option value={legacy}>{legacy}</option>}
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </optgroup>
          ))}
        </>
      ) : (
        opts.map((o) => (
          <option key={o} value={o}>{labels?.[o] ?? o}</option>
        ))
      )}
    </TextField>
  );
};

/** Secondary 30px tool button, with spinner and an optional "fixes an open issue" dot. */
export const ToolButton: React.FC<
  ButtonProps & { running?: boolean; recommended?: boolean; tooltip?: string }
> = ({ running, recommended, tooltip, children, disabled, sx, ...rest }) => (
  <Button
    variant="outlined"
    size="small"
    title={tooltip}
    disabled={disabled || running}
    {...rest}
    sx={{
      height: 30,
      px: '11px',
      gap: '7px',
      borderRadius: `${tokens.radius.small}px`,
      fontSize: 12.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      color: tokens.ink.primary,
      borderColor: recommended ? tokens.signal.caution.border : tokens.line.default,
      bgcolor: recommended ? tokens.signal.caution.row : tokens.surface.card,
      '&:hover': { borderColor: tokens.accent.border, bgcolor: tokens.signal.info.row },
      ...sx,
    }}
  >
    {running && <CircularProgress size={11} thickness={6} color="inherit" />}
    {recommended && !running && <Dot tone="caution" />}
    {children}
  </Button>
);

/** Text-only link button in accent, 12.5/600. */
export const LinkButton: React.FC<ButtonProps> = ({ sx, ...rest }) => (
  <Button
    variant="text"
    size="small"
    {...rest}
    sx={{ minWidth: 0, p: 0, fontSize: 12.5, fontWeight: 600, color: tokens.accent.main, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }, ...sx }}
  />
);

/** Caution banner with an optional action on the right. */
export const WarningBanner: React.FC<{ title?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode; tone?: SignalTone }> = ({
  title,
  children,
  action,
  tone = 'caution',
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      px: '13px',
      py: '10px',
      border: `1px solid ${tokens.signal[tone].border}`,
      bgcolor: tokens.signal[tone].tint,
      borderRadius: '9px',
      flexWrap: 'wrap',
    }}
  >
    <Box sx={{ mt: '6px' }}><Dot tone={tone} /></Box>
    <Box sx={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {title && <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal[tone].ink }}>{title}</Typography>}
      <Typography component="span" sx={{ fontSize: 12.5, lineHeight: 1.5, color: tokens.signal[tone].ink }}>{children}</Typography>
    </Box>
    {action}
  </Box>
);

/** The small outlined button used inside a banner. */
export const BannerAction: React.FC<ButtonProps & { tone?: SignalTone }> = ({ tone = 'caution', sx, ...rest }) => (
  <Button
    variant="outlined"
    size="small"
    {...rest}
    sx={{
      height: 28,
      px: 1.25,
      borderRadius: '7px',
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      color: tokens.signal[tone].ink,
      borderColor: tokens.signal[tone].border,
      bgcolor: tokens.surface.card,
      ...sx,
    }}
  />
);

/** Segmented stat strip (MSRP · MAP · Street avg; Low · Average · High · Your retail). */
export const StatStrip: React.FC<{ items: Array<{ label: string; value: React.ReactNode; note?: React.ReactNode; highlight?: boolean }> }> = ({ items }) => (
  <Box sx={{ display: 'flex', border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px`, overflow: 'hidden', flexWrap: 'wrap' }}>
    {items.map((item, i) => (
      <Box
        key={item.label}
        sx={{
          flex: '1 0 auto',
          minWidth: 96,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          px: '14px',
          py: 1,
          borderLeft: i === 0 ? 'none' : `1px solid ${tokens.line.default}`,
          bgcolor: item.highlight ? tokens.surface.sunken : tokens.surface.card,
        }}
      >
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.ink.faint }}>
          {item.label}
        </Typography>
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>
          {item.value}
        </Typography>
        {item.note && <Typography component="span" sx={{ fontSize: 11, color: tokens.ink.faint }}>{item.note}</Typography>}
      </Box>
    ))}
  </Box>
);

/** Key/value rows with a mono key column (Record, Source, Fingerprints). */
export const KeyValueRows: React.FC<{ rows: Array<{ k: string; v: React.ReactNode }>; keyWidth?: string; mono?: boolean }> = ({
  rows,
  keyWidth = 'minmax(110px, 150px)',
  mono = true,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    {rows.map((r) => (
      <Box
        key={r.k}
        sx={{ display: 'grid', gridTemplateColumns: `${keyWidth} minmax(0, 1fr)`, gap: 1.5, alignItems: 'baseline', py: 1, borderBottom: `1px solid ${tokens.line.faint}` }}
      >
        <Typography component="span" sx={mono ? { fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.faint } : { fontSize: 12, color: tokens.ink.faint }}>
          {r.k}
        </Typography>
        <Typography component="span" sx={{ fontSize: 13, color: r.v === EMPTY || r.v == null || r.v === '' ? tokens.ink.faint : tokens.ink.primary, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
          {r.v == null || r.v === '' ? EMPTY : r.v}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Editable chip list (pricing aliases, tags): × to remove, dashed input, Enter adds. */
export const ChipList: React.FC<{
  label: string;
  hint?: React.ReactNode;
  items: string[];
  editing: boolean;
  placeholder: string;
  onChange: (next: string[]) => void;
}> = ({ label, hint, items, editing, placeholder, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const value = draft.trim();
    if (!value) return;
    if (!items.includes(value)) onChange([...items, value]);
    setDraft('');
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', px: '18px', pb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <FieldLabel>{label}</FieldLabel>
        {hint && <Typography component="span" sx={{ fontSize: 11.5, color: tokens.ink.faint }}>{hint}</Typography>}
      </Stack>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {items.length === 0 && !editing && <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.faint }}>{EMPTY}</Typography>}
        {items.map((item) => (
          <Box
            key={item}
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 24, px: 1, borderRadius: '7px', bgcolor: tokens.surface.tint, color: tokens.ink.body, fontSize: 12 }}
          >
            {item}
            {editing && (
              <Box
                component="button"
                type="button"
                aria-label={`Remove ${item}`}
                onClick={() => onChange(items.filter((x) => x !== item))}
                sx={{ border: 'none', background: 'none', p: '0 0 0 2px', fontSize: 14, lineHeight: 1, color: tokens.ink.faint, cursor: 'pointer', '&:hover': { color: tokens.signal.problem.ink } }}
              >
                ×
              </Box>
            )}
          </Box>
        ))}
        {editing && (
          <Box
            component="input"
            value={draft}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            sx={{
              width: 150,
              height: 26,
              px: 1,
              border: `1px dashed ${tokens.ink.disabled}`,
              borderRadius: '7px',
              font: 'inherit',
              fontSize: 12,
              outline: 'none',
              '&:focus': { borderColor: tokens.accent.main, borderStyle: 'solid' },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

/** Inline "×" remove control for editable rows. */
export const RemoveX: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
  <Box
    component="button"
    type="button"
    aria-label={label}
    onClick={onClick}
    sx={{ border: 'none', background: 'none', px: 0.5, fontSize: 16, color: tokens.ink.faint, cursor: 'pointer', '&:hover': { color: tokens.signal.problem.ink } }}
  >
    ×
  </Box>
);
